import useSWR from 'swr';

import { getCaseInsightsStatus, type InsightsStatusResponse } from '@/api/case-images';
import { ENDPOINTS } from '@/api/endpoints';

const POLL_INTERVAL = 15000;

/**
 * Extended status type including computed states
 */
export type OverallInsightsStatus =
  | 'idle'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'partial_success';

/**
 * Progress statistics for insights processing
 */
export interface InsightsProgress {
  total: number;
  completed: number;
  processing: number;
  failed: number;
  notStarted: number;
}

/**
 * Props for useInsightsStatus hook
 */
interface UseInsightsStatusProps {
  caseId: string;
  enabled: boolean;
}

/**
 * Return type for useInsightsStatus hook
 */
interface UseInsightsStatusReturn {
  data: InsightsStatusResponse | undefined;
  progress: InsightsProgress;
  overallStatus: OverallInsightsStatus;
  isComplete: boolean;
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * Custom hook to fetch and monitor Insights processing status for a case
 * Automatically polls the backend every 15 seconds when enabled
 *
 * @param caseId - The case ID to fetch insights status for
 * @param enabled - Whether to enable polling (when false, no requests are made)
 * @returns Object with insights data, progress statistics, and overall status
 */
export function useInsightsStatus({
  caseId,
  enabled,
}: UseInsightsStatusProps): UseInsightsStatusReturn {
  // Only create SWR key if enabled
  const key = enabled ? [ENDPOINTS.CASE_IMAGES_INSIGHTS_STATUS, caseId] : null;

  const { data, error, isLoading } = useSWR<InsightsStatusResponse, Error>(
    key,
    () => getCaseInsightsStatus(caseId),
    {
      refreshInterval: POLL_INTERVAL, // Poll every 15 seconds
      revalidateOnFocus: false, // Don't refetch when window gains focus
      revalidateOnReconnect: false, // Don't refetch on network reconnect
      dedupingInterval: POLL_INTERVAL, // Prevent duplicate requests within 15s
      shouldRetryOnError: false, // Don't retry on API errors
    }
  );

  // Calculate progress statistics from image status array
  const progress: InsightsProgress = {
    total: data?.data.length ?? 0,
    completed: data?.data.filter((img) => img.status === 'completed').length ?? 0,
    processing: data?.data.filter((img) => img.status === 'processing').length ?? 0,
    failed: data?.data.filter((img) => img.status === 'failed').length ?? 0,
    notStarted: data?.data.filter((img) => img.status === 'not_started').length ?? 0,
  };

  // Determine overall status based on aggregated image states
  let overallStatus: OverallInsightsStatus = 'idle';

  if (progress.total > 0) {
    const hasProcessing = progress.processing > 0 || progress.notStarted > 0;
    const allCompleted = progress.completed === progress.total;
    const allFailed = progress.failed === progress.total;
    const hasMixed = progress.completed > 0 && progress.failed > 0;

    if (hasProcessing) {
      overallStatus = 'processing';
    } else if (allCompleted) {
      overallStatus = 'completed';
    } else if (allFailed) {
      overallStatus = 'failed';
    } else if (hasMixed) {
      overallStatus = 'partial_success';
    }
  }

  // Determine if processing is complete (success, failure, or partial)
  const isComplete =
    progress.total > 0 &&
    progress.processing === 0 &&
    progress.notStarted === 0 &&
    (overallStatus === 'completed' ||
      overallStatus === 'failed' ||
      overallStatus === 'partial_success');

  return {
    data,
    progress,
    overallStatus,
    isComplete,
    isLoading,
    error,
  };
}
